import { useUnit } from 'effector-react';
import { Button, Card, Space, Switch, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import style from './MapGradient.module.css';
import {
  $blockedBuckets,
  $gradientBuckets,
} from '../../models/gradientModel/index.js';
import { $mapLoaded } from '../../models/mapModel/index.js';
import { formatNumberCompact } from '../../utils/format-utils.js';
import useWindowSize from '../../hooks/useWindowSize.js';
import { layers_dictionary } from '../../dictionaries/layers_dictionary.js';
import { $activeMetric, $language } from '../../models/logicModel/index.js';
import {
  $activeFilters,
  changeActiveFilterEv,
} from '../../models/activeFiltersModel/index.js';
import { static_values } from '../../dictionaries/gradient_dictionary.js';

export default function MapGradient() {
  const mapLoaded = useUnit($mapLoaded);
  const gradientBuckets = useUnit($gradientBuckets);
  const blockedBuckets = useUnit($blockedBuckets);
  const { pathname } = useLocation();
  const hideNumbers = false;
  const [blockLevel, setBlockLevel] = useState('zoom_8');
  const [width, height] = useWindowSize();
  const [expanded, setExpanded] = useState(true);
  const activeMetric = useUnit($activeMetric);
  const changeActiveFilter = useUnit(changeActiveFilterEv);
  const activeGradientFilter = useUnit($activeFilters).gradient;
  const language = useUnit($language);
  const { t } = useTranslation();

  let bucketsCopy = gradientBuckets.buckets;
  let colorsCopy = gradientBuckets.colors;

  if (activeMetric === 'flood_ind' || activeMetric === 'mudflow_ind') {
    bucketsCopy = static_values[activeMetric].buckets;
    colorsCopy = static_values[activeMetric].colors;
  }
  useEffect(() => {
    if (window.map) {
      if (mapLoaded) {
        window.map.on('render', () => {
          const zoom = window.map.getZoom();
          let level;
          if (zoom < 11) {
            level = 'zoom_8';
          } else if (zoom >= 11 && zoom < 13) {
            level = 'zoom_9';
          } else {
            level = 'zoom_10';
          }
          if (blockLevel !== level) {
            setBlockLevel(level);
          }
        });
      }
    }
  }, [mapLoaded]);

  const titleKey = layers_dictionary[activeMetric];
  const title = titleKey.replace(
    titleKey.split(' ')[0],
    t(titleKey.split(' ')[0])
  );

  return (
    <div className={style.bottom_bar_wrapper}>
      <Card
        className={[style.gradient_wrapper, 'glass'].join(' ')}
        bodyStyle={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div>
          <div
            className={style.gradient_top}
            style={{
              justifyContent: 'center',
            }}
          >
            <div className={style.gradient_by_wrapper}>
              <Tooltip title={title} placement="top">
                <div
                  style={{
                    color: 'black',
                  }}
                  className={style.gradient_title}
                >
                  {title}
                </div>
              </Tooltip>
            </div>
          </div>
          <Space
            size="small"
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: width < 500 ? 'wrap' : 'nowrap',
              rowGap: 20,
            }}
          >
            {hideNumbers && (
              <div className={style.bucket_wrapper}>
                <div className={style.bucket} style={{ color: 'black' }}>
                  Низкий
                </div>
              </div>
            )}
            {bucketsCopy.map((item, index) => {
              if (index !== bucketsCopy.length - 1) {
                return (
                  <>
                    {!hideNumbers && (
                      <div className={style.bucket_wrapper}>
                        <div
                          className={style.bucket}
                          style={{ color: 'black' }}
                        >
                          {formatNumberCompact(item, language)}
                        </div>
                      </div>
                    )}
                    <GradientItem
                      key={`${item}${index}`}
                      maxValue={bucketsCopy[index + 1] || 0}
                      minValue={item}
                      color={colorsCopy[index]}
                      blockedBuckets={blockedBuckets[blockLevel]}
                      isLast={index === bucketsCopy.length - 2}
                      changeActiveFilter={changeActiveFilter}
                      activeGradientFilter={activeGradientFilter}
                    />
                    {index === bucketsCopy.length - 2 && !hideNumbers && (
                      <div className={style.bucket_wrapper}>
                        <div
                          className={style.bucket}
                          style={{ color: 'black' }}
                        >
                          {bucketsCopy[index + 1] === Infinity
                            ? '∞'
                            : formatNumberCompact(
                                bucketsCopy[index + 1],
                                language
                              )}
                        </div>
                      </div>
                    )}
                  </>
                );
              }
            })}
            {hideNumbers && (
              <div className={style.bucket_wrapper}>
                <div className={style.bucket} style={{ color: 'black' }}>
                  Высокий
                </div>
              </div>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
}

function GradientItem({
  color,
  maxValue,
  minValue,
  changeActiveFilter,
  activeGradientFilter,
}) {
  const onClick = () => {
    changeActiveFilter({
      field: 'gradient',
      value: {
        min: minValue,
        max: maxValue,
      },
    });
  };

  return (
    <div
      className={[
        style.gradient_item,
        activeGradientFilter.some(
          (item) => item.min === minValue && item.max === maxValue
        )
          ? style.active
          : '',
      ].join(' ')}
      onClick={onClick}
      style={{
        background: color,
      }}
    />
  );
}
